import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Briefcase,
  Building2,
  Check,
  Copy,
  Gavel,
  Key,
  Mail,
  Pencil,
  Plus,
  Power,
  Search,
  Shield,
  Sparkles,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { api, ApiError } from "../../api";
import { Button, Card, Field, cx } from "../../ui";

type Building = { id: string; name: string };
type Unit = { id: string; unitNumber: string };
type RoleCode =
  | "unit_owner"
  | "board_member"
  | "property_manager"
  | "chief_engineer"
  | "admin"
  | "contractor_primary_contact";

const ROLE_OPTIONS: ReadonlyArray<{ value: RoleCode; label: string }> = [
  { value: "unit_owner", label: "Unit Owner" },
  { value: "board_member", label: "Board Member" },
  { value: "property_manager", label: "Property Manager" },
  { value: "chief_engineer", label: "Chief Engineer" },
  { value: "admin", label: "Administrator" },
];
const ROLE_LABEL: Record<string, string> = Object.fromEntries(
  ROLE_OPTIONS.map((o) => [o.value, o.label]),
);

type UserListRow = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  isActive: boolean;
  emailVerifiedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  roleCodes: string[];
  unitCount: number;
  unitNumbers: string[];
  /** Resolved unit references so list-row actions like
   *  "Start project" and "Upload insurance" can deep-link
   *  without forcing the admin to open the edit panel first.
   *  Empty when the user owns no units. */
  units: Array<{
    unitId: string;
    unitNumber: string;
    buildingId: string;
  }>;
  /** True when the user is owner / submitter / on-behalf-of for at
   *  least one project that is not closed / completed / rejected. */
  hasActiveProject: boolean;
  /** True when the user owns at least one unit but none of those
   *  units has a homeowner liability policy with an expiration date
   *  in the future. */
  missingInsurance: boolean;
};

type RoleGrant = {
  id: string;
  roleCode: string;
  roleName: string;
  buildingId: string;
  buildingName: string;
  grantedAt: string;
  /** Synthesized from an active ownership when no explicit
   *  user_roles row exists. Implicit grants can't be revoked
   *  (no real row to revoke); end the underlying ownership to
   *  remove them. */
  implicit?: boolean;
};

type Ownership = {
  id: string;
  unitId: string;
  unitNumber: string;
  buildingId: string;
  buildingName: string;
  ownershipType: string;
  startedAt: string;
};

type UserDetail = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  isActive: boolean;
  emailVerifiedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  roles: RoleGrant[];
  ownerships: Ownership[];
};

type FilterState = {
  roles: ReadonlySet<RoleCode>;
  hasActiveProject: boolean;
  missingInsurance: boolean;
};

const EMPTY_FILTER: FilterState = {
  roles: new Set(),
  hasActiveProject: false,
  missingInsurance: false,
};

export function AdminUsers() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<UserListRow[] | null>(null);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState<FilterState>(EMPTY_FILTER);

  const [buildings, setBuildings] = useState<Building[]>([]);

  useEffect(() => {
    void reload();
    void api
      .get<{ data: Building[] }>("/api/buildings")
      .then((r) => setBuildings(r.data))
      .catch(() => setBuildings([]));
  }, []);

  const reload = async (q?: string) => {
    const url =
      q && q.trim().length > 0
        ? `/api/admin/users?q=${encodeURIComponent(q.trim())}`
        : "/api/admin/users";
    const r = await api.get<{ data: UserListRow[] }>(url);
    setRows(r.data);
  };

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    void reload(search);
  };

  const filteredRows = useMemo(() => {
    if (!rows) return null;
    const activeFilter =
      filter.roles.size > 0 ||
      filter.hasActiveProject ||
      filter.missingInsurance;
    if (!activeFilter) return rows;
    return rows.filter((r) => {
      if (filter.roles.size > 0) {
        const matches = r.roleCodes.some((c) =>
          filter.roles.has(c as RoleCode),
        );
        if (!matches) return false;
      }
      if (filter.hasActiveProject && !r.hasActiveProject) return false;
      if (filter.missingInsurance && !r.missingInsurance) return false;
      return true;
    });
  }, [rows, filter]);

  const toggleRole = (code: RoleCode) => {
    setFilter((f) => {
      const next = new Set(f.roles);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return { ...f, roles: next };
    });
  };

  const filterActive =
    filter.roles.size > 0 ||
    filter.hasActiveProject ||
    filter.missingInsurance;

  return (
    <div>
      <Card className="!p-0 overflow-hidden">
        <div className="flex items-center gap-3 p-5 sm:p-6 border-b border-sand-100 flex-wrap">
          <div className="flex-1 min-w-0">
            <h2 className="text-ink-800 m-0">Users</h2>
            <p className="text-xs text-ink-500 mt-0.5">
              Create new users, update profile information, and manage role
              and unit assignments.
            </p>
          </div>
          <form onSubmit={onSearch} className="relative flex-1 min-w-[200px] max-w-[280px]">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or email…"
              className="input pl-9 h-[37px] py-0"
            />
          </form>
          <Button
            variant="primary"
            onClick={() => setCreating(true)}
          >
            <UserPlus size={14} /> New user
          </Button>
        </div>

        <div className="flex items-center gap-2 px-5 sm:px-6 py-3 border-b border-sand-100 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-ink-400 mr-1">
            Role
          </span>
          {ROLE_OPTIONS.map((o) => (
            <FilterPill
              key={o.value}
              active={filter.roles.has(o.value)}
              onClick={() => toggleRole(o.value)}
            >
              {o.label}
            </FilterPill>
          ))}
          <span className="w-px h-5 bg-sand-200 mx-1" />
          <FilterPill
            active={filter.hasActiveProject}
            icon={<Briefcase size={11} />}
            onClick={() =>
              setFilter((f) => ({ ...f, hasActiveProject: !f.hasActiveProject }))
            }
          >
            Has active project
          </FilterPill>
          <FilterPill
            active={filter.missingInsurance}
            icon={<AlertTriangle size={11} />}
            onClick={() =>
              setFilter((f) => ({
                ...f,
                missingInsurance: !f.missingInsurance,
              }))
            }
          >
            Missing insurance
          </FilterPill>
          {filterActive && (
            <button
              type="button"
              onClick={() => setFilter(EMPTY_FILTER)}
              className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-[12px] text-ink-500 hover:text-ink-800"
            >
              <X size={12} /> Clear filters
            </button>
          )}
        </div>

        {rows === null ? (
          <p className="p-6 text-ink-500 text-sm">Loading…</p>
        ) : filteredRows && filteredRows.length === 0 ? (
          <p className="p-6 text-ink-500 text-sm">
            {filterActive ? "No users match the current filters." : "No users match."}
          </p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="whitespace-nowrap">Name</th>
                  <th className="whitespace-nowrap">Email</th>
                  <th className="whitespace-nowrap">Roles</th>
                  <th className="whitespace-nowrap">Board</th>
                  <th className="whitespace-nowrap">Units</th>
                  <th className="whitespace-nowrap">Projects</th>
                  <th className="whitespace-nowrap">Insurance</th>
                  <th className="whitespace-nowrap">Active</th>
                  <th className="whitespace-nowrap">Last login</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(filteredRows ?? []).map((r) => (
                  <tr
                    key={r.id}
                    className=""
                  >
                    <td className="font-medium text-ink-800">
                      <Link
                        to={`/admin/users/${r.id}`}
                        className="text-ink-800 hover:text-brand-700 no-underline hover:no-underline"
                        title="Open this user's full record"
                      >
                        {r.firstName} {r.lastName}
                      </Link>
                    </td>
                    <td className="text-ink-600">{r.email}</td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {r.roleCodes
                          .filter((c) => c !== "board_member")
                          .map((c) => (
                            <span
                              key={c}
                              className="badge bg-brand-100 text-brand-700 whitespace-nowrap"
                            >
                              {ROLE_LABEL[c] ?? c}
                            </span>
                          ))}
                      </div>
                    </td>
                    <td>
                      {r.roleCodes.includes("board_member") ? (
                        <span
                          className="inline-flex items-center gap-1 badge bg-status-review-bg text-status-review-fg whitespace-nowrap"
                          title="Board Member"
                        >
                          <Gavel size={11} /> Board
                        </span>
                      ) : (
                        <span className="text-ink-300">—</span>
                      )}
                    </td>
                    <td>
                      {r.unitNumbers.length === 0 ? (
                        <span className="text-ink-400">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {r.unitNumbers.slice(0, 4).map((n) => (
                            <span
                              key={n}
                              className="badge bg-sand-100 text-ink-700"
                            >
                              {n}
                            </span>
                          ))}
                          {r.unitNumbers.length > 4 && (
                            <span className="badge bg-sand-100 text-ink-500">
                              +{r.unitNumbers.length - 4}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      {r.hasActiveProject ? (
                        <span
                          className="inline-flex items-center gap-1 badge bg-status-approved-bg text-status-approved-fg whitespace-nowrap"
                          title="Has at least one project that isn't closed, completed, or rejected"
                        >
                          <Briefcase size={11} /> active
                        </span>
                      ) : (
                        <span className="text-ink-300">—</span>
                      )}
                    </td>
                    <td>
                      {r.unitNumbers.length === 0 ? (
                        <span className="text-ink-300">n/a</span>
                      ) : r.missingInsurance ? (
                        <span
                          className="inline-flex items-center gap-1 badge bg-status-rejected-bg text-status-rejected-fg whitespace-nowrap"
                          title="This owner has no liability policy with a future expiration date on any of their units"
                        >
                          <AlertTriangle size={11} /> missing
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 badge bg-status-approved-bg text-status-approved-fg whitespace-nowrap"
                          title="At least one unit has a current liability policy"
                        >
                          <Shield size={11} /> on file
                        </span>
                      )}
                    </td>
                    <td>
                      {r.isActive ? (
                        <span className="badge bg-status-approved-bg text-status-approved-fg whitespace-nowrap">
                          active
                        </span>
                      ) : (
                        <span className="badge bg-sand-100 text-ink-600 whitespace-nowrap">
                          inactive
                        </span>
                      )}
                    </td>
                    <td className="text-xs text-ink-500 whitespace-nowrap">
                      {r.lastLoginAt
                        ? new Date(r.lastLoginAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td>
                      <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                        {/*
                          White-glove actions on the list row so an
                          admin doesn't need to expand the edit
                          panel to start a project for an owner or
                          drop an insurance certificate on their
                          behalf. Both actions require at least one
                          assigned unit.
                          - Start project: deep-links the wizard
                            with the owner + unit pre-selected.
                            Only when the user has exactly one
                            unit (multi-unit users go through the
                            edit panel where the per-row Start
                            project link disambiguates).
                          - Upload insurance: links to the per-user
                            insurance page, which already handles
                            the multi-unit case with its own
                            picker.
                        */}
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() =>
                            navigate(`/admin/users/${r.id}/edit`)
                          }
                        >
                          <Pencil size={13} /> Edit
                        </Button>
                        {r.units.length === 1 && (
                          <Link
                            to={`/projects/new?onBehalfOfUserId=${r.id}&unitId=${r.units[0]!.unitId}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11.5px] text-brand-700 hover:text-brand-800 hover:bg-brand-50 no-underline hover:no-underline"
                            title="Start a white-glove project for this owner"
                          >
                            <Sparkles size={11} /> Start project
                          </Link>
                        )}
                        {r.units.length > 0 && (
                          <Link
                            to={`/admin/users/${r.id}/insurance`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11.5px] text-brand-700 hover:text-brand-800 hover:bg-brand-50 no-underline hover:no-underline"
                            title="Upload an insurance certificate on this owner's behalf"
                          >
                            <Shield size={11} /> Upload insurance
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating && (
        <CreateUserCard
          buildings={buildings}
          onCancel={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false);
            await reload(search);
          }}
        />
      )}

    </div>
  );
}

// ---------------------------------------------------------------------------
// Create user card
// ---------------------------------------------------------------------------
function CreateUserCard({
  buildings,
  onCancel,
  onCreated,
}: {
  buildings: Building[];
  onCancel: () => void;
  onCreated: () => void | Promise<void>;
}) {
  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    password: "",
    isActive: true,
  });
  const [grants, setGrants] = useState<
    Array<{
      buildingId: string;
      roleCode: RoleCode;
      unitId: string;
    }>
  >([]);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setTempPassword(null);
    try {
      const payload = {
        email: form.email.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim() || undefined,
        password: form.password.trim() || undefined,
        isActive: form.isActive,
        grants: grants
          .filter((g) => g.buildingId && g.roleCode)
          .map((g) => ({
            buildingId: g.buildingId,
            roleCode: g.roleCode,
            // Forward unitId for both unit_owner (required) and
            // board_member (recommended). The server creates a
            // unit_owners row in either case so a Board Member
            // who's also an owner doesn't need a separate
            // grant later.
            ...((g.roleCode === "unit_owner" ||
              g.roleCode === "board_member") &&
            g.unitId
              ? { unitId: g.unitId }
              : {}),
          })),
      };
      const res = await api.post<{
        data: { id: string };
        temporaryPassword: string | null;
      }>("/api/admin/users", payload);
      setTempPassword(res.temporaryPassword);
      // If we generated a temp password, keep the panel open so the
      // admin can copy it. Otherwise close immediately.
      if (!res.temporaryPassword) {
        await onCreated();
      } else {
        // Refresh the list now; admin will dismiss this card manually.
        await onCreated();
      }
    } catch (err) {
      if (err instanceof ApiError) {
        const code = (err.body as { error?: string })?.error;
        setError(
          code === "email_in_use"
            ? "An account with that email already exists."
            : code === "unknown_role"
              ? "One of the role codes is invalid."
              : `Create failed: ${code ?? err.status}`,
        );
      } else setError("Create failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="flex items-start justify-between gap-3 mb-4">
        <h3 className="text-ink-800 m-0">New user</h3>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          <X size={13} /> Cancel
        </Button>
      </div>

      {tempPassword && (
        <TempPasswordCallout
          password={tempPassword}
          onDismiss={() => {
            setTempPassword(null);
            onCancel();
          }}
        />
      )}

      <form onSubmit={onSubmit}>
        <div className="field-row">
          <Field label="First name *">
            <input
              className="input"
              value={form.firstName}
              onChange={(e) => setForm({ ...form, firstName: e.target.value })}
              required
            />
          </Field>
          <Field label="Last name *">
            <input
              className="input"
              value={form.lastName}
              onChange={(e) => setForm({ ...form, lastName: e.target.value })}
              required
            />
          </Field>
        </div>
        <div className="field-row">
          <Field label="Email *">
            <input
              className="input"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </Field>
          <Field label="Phone">
            <input
              className="input"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </Field>
        </div>
        <Field
          label="Initial password"
          hint="Leave blank to auto-generate a one-time temporary password (shown once on save)."
        >
          <input
            className="input"
            type="text"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Min 12 characters"
            minLength={12}
          />
        </Field>
        <label className="flex items-center gap-2 text-sm text-ink-700 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            className="rounded border-ink-300 text-brand-700 focus:ring-brand-500"
          />
          Active
        </label>

        <RoleGrantsEditor
          buildings={buildings}
          grants={grants}
          setGrants={setGrants}
        />

        {error && <div className="alert-error">{error}</div>}

        <Button type="submit" variant="primary" loading={busy}>
          <UserPlus size={14} /> Create user
        </Button>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Edit user card
//
// Exported so the dedicated edit route (/admin/users/:userId/edit) can
// render the same form on its own page instead of expanding inline on
// the list. The list now navigates to that route on Edit.
// ---------------------------------------------------------------------------
export function EditUserCard({
  userId,
  buildings,
  onClose,
  onChanged,
}: {
  userId: string;
  buildings: Building[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const [user, setUser] = useState<UserDetail | null>(null);
  const [profile, setProfile] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    isActive: boolean;
  } | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSaved, setProfileSaved] = useState(false);

  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [resettingPwd, setResettingPwd] = useState(false);

  const reload = async () => {
    const r = await api.get<{ data: UserDetail }>(`/api/admin/users/${userId}`);
    setUser(r.data);
    setProfile({
      firstName: r.data.firstName,
      lastName: r.data.lastName,
      email: r.data.email,
      phone: r.data.phone ?? "",
      isActive: r.data.isActive,
    });
  };

  useEffect(() => {
    setUser(null);
    setProfile(null);
    setTempPassword(null);
    void reload();
  }, [userId]);

  const saveProfile = async (e: FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    setSavingProfile(true);
    setProfileError(null);
    setProfileSaved(false);
    try {
      await api.patch(`/api/admin/users/${userId}`, {
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        phone: profile.phone || null,
        isActive: profile.isActive,
      });
      setProfileSaved(true);
      await reload();
      onChanged();
    } catch (err) {
      if (err instanceof ApiError) {
        const code = (err.body as { error?: string })?.error;
        setProfileError(
          code === "email_in_use"
            ? "Another active account uses that email."
            : `Save failed: ${code ?? err.status}`,
        );
      } else setProfileError("Save failed.");
    } finally {
      setSavingProfile(false);
    }
  };

  const resetPassword = async () => {
    setResettingPwd(true);
    setTempPassword(null);
    try {
      const res = await api.post<{
        ok: boolean;
        temporaryPassword: string | null;
      }>(`/api/admin/users/${userId}/set-password`, {});
      if (res.temporaryPassword) setTempPassword(res.temporaryPassword);
    } catch {
      // surfaced inline above
    } finally {
      setResettingPwd(false);
    }
  };

  if (!user || !profile) {
    return (
      <Card>
        <p className="text-ink-500 text-sm">Loading user…</p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-ink-800 m-0">
            Edit{" "}
            <span className="text-brand-700">
              {user.firstName} {user.lastName}
            </span>
          </h3>
          <p className="text-[11.5px] text-ink-500 mt-0.5">{user.email}</p>
        </div>
        <Button size="sm" variant="ghost" onClick={onClose}>
          <X size={13} /> Close
        </Button>
      </div>

      {tempPassword && (
        <TempPasswordCallout
          password={tempPassword}
          onDismiss={() => setTempPassword(null)}
        />
      )}

      <form onSubmit={saveProfile} className="mb-6">
        <div className="text-[11px] font-semibold tracking-wider uppercase text-ink-400 mb-3">
          Profile
        </div>
        <div className="field-row">
          <Field label="First name">
            <input
              className="input"
              value={profile.firstName}
              onChange={(e) =>
                setProfile({ ...profile, firstName: e.target.value })
              }
              required
            />
          </Field>
          <Field label="Last name">
            <input
              className="input"
              value={profile.lastName}
              onChange={(e) =>
                setProfile({ ...profile, lastName: e.target.value })
              }
              required
            />
          </Field>
        </div>
        <div className="field-row">
          <Field label="Email">
            <input
              className="input"
              type="email"
              value={profile.email}
              onChange={(e) =>
                setProfile({ ...profile, email: e.target.value })
              }
              required
            />
          </Field>
          <Field label="Phone">
            <input
              className="input"
              value={profile.phone}
              onChange={(e) =>
                setProfile({ ...profile, phone: e.target.value })
              }
            />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-ink-700 mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={profile.isActive}
            onChange={(e) =>
              setProfile({ ...profile, isActive: e.target.checked })
            }
            className="rounded border-ink-300 text-brand-700 focus:ring-brand-500"
          />
          <Power size={13} className="text-ink-500" />
          Active (sign-in allowed)
        </label>
        {profileSaved && <div className="alert-success">Profile saved.</div>}
        {profileError && <div className="alert-error">{profileError}</div>}
        <div className="flex gap-2">
          <Button type="submit" variant="primary" loading={savingProfile}>
            <Check size={14} /> Save profile
          </Button>
          <Button
            type="button"
            variant="secondary"
            loading={resettingPwd}
            onClick={() => void resetPassword()}
          >
            <Key size={14} /> Reset password
          </Button>
        </div>
      </form>

      <div className="border-t border-sand-100 pt-5 mb-6">
        <div className="text-[11px] font-semibold tracking-wider uppercase text-ink-400 mb-3">
          Roles
        </div>
        <RolesList
          userId={userId}
          buildings={buildings}
          grants={user.roles}
          onChanged={async () => {
            await reload();
            onChanged();
          }}
        />
      </div>

      <div className="border-t border-sand-100 pt-5">
        <div className="text-[11px] font-semibold tracking-wider uppercase text-ink-400 mb-3">
          Units owned
        </div>
        <OwnershipsList
          userId={userId}
          buildings={buildings}
          ownerships={user.ownerships}
          onChanged={async () => {
            await reload();
            onChanged();
          }}
        />
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Role grants editor — used both inline in the create form and as a
// stand-alone block on the edit panel (with persistent grants).
// ---------------------------------------------------------------------------
function RoleGrantsEditor({
  buildings,
  grants,
  setGrants,
}: {
  buildings: Building[];
  grants: Array<{
    buildingId: string;
    roleCode: RoleCode;
    unitId: string;
  }>;
  setGrants: (
    g: Array<{ buildingId: string; roleCode: RoleCode; unitId: string }>,
  ) => void;
}) {
  const add = () =>
    setGrants([
      ...grants,
      {
        buildingId: buildings[0]?.id ?? "",
        roleCode: "unit_owner",
        unitId: "",
      },
    ]);
  const update = (
    i: number,
    patch: Partial<{ buildingId: string; roleCode: RoleCode; unitId: string }>,
  ) =>
    setGrants(grants.map((g, idx) => (idx === i ? { ...g, ...patch } : g)));
  const remove = (i: number) =>
    setGrants(grants.filter((_, idx) => idx !== i));

  return (
    <div className="mb-4">
      <div className="text-[11px] font-semibold tracking-wider uppercase text-ink-400 mb-2">
        Initial role grants
      </div>
      {grants.length === 0 ? (
        <p className="text-[12px] text-ink-400 mb-2">
          No roles yet. The user can sign in but won't see any building-scoped
          surfaces.
        </p>
      ) : (
        <ul className="flex flex-col gap-2 mb-2">
          {grants.map((g, i) => (
            <GrantRow
              key={i}
              grant={g}
              buildings={buildings}
              onChange={(patch) => update(i, patch)}
              onRemove={() => remove(i)}
            />
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] text-brand-700 hover:bg-brand-50"
      >
        <Plus size={13} /> Add role grant
      </button>
    </div>
  );
}

function GrantRow({
  grant,
  buildings,
  onChange,
  onRemove,
}: {
  grant: { buildingId: string; roleCode: RoleCode; unitId: string };
  buildings: Building[];
  onChange: (
    patch: Partial<{
      buildingId: string;
      roleCode: RoleCode;
      unitId: string;
    }>,
  ) => void;
  onRemove: () => void;
}) {
  const [units, setUnits] = useState<Unit[]>([]);
  useEffect(() => {
    if (!grant.buildingId) return;
    void api
      .get<{ data: Unit[] }>(`/api/buildings/${grant.buildingId}/units`)
      .then((r) => setUnits(r.data))
      .catch(() => setUnits([]));
  }, [grant.buildingId]);

  return (
    <li className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
      <Field label="Building">
        <select
          className="select"
          value={grant.buildingId}
          onChange={(e) => onChange({ buildingId: e.target.value, unitId: "" })}
        >
          {buildings.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Role">
        <select
          className="select"
          value={grant.roleCode}
          onChange={(e) =>
            onChange({ roleCode: e.target.value as RoleCode, unitId: "" })
          }
        >
          {ROLE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>
      {/*
        Both unit_owner and board_member can carry a unit. Most
        Board Members are also unit owners (they just can't vote
        on or approve their own submissions, which the recusal
        logic handles). For other roles the field is disabled
        with a "n/a" label.
      */}
      <Field
        label={
          grant.roleCode === "unit_owner"
            ? "Unit *"
            : grant.roleCode === "board_member"
              ? "Unit (recommended)"
              : "Unit (n/a)"
        }
      >
        <select
          className="select"
          value={grant.unitId}
          onChange={(e) => onChange({ unitId: e.target.value })}
          disabled={
            grant.roleCode !== "unit_owner" &&
            grant.roleCode !== "board_member"
          }
          required={grant.roleCode === "unit_owner"}
        >
          <option value="">
            {grant.roleCode === "unit_owner"
              ? "Select a unit…"
              : grant.roleCode === "board_member"
                ? "No unit (rare)"
                : "—"}
          </option>
          {units.map((u) => (
            <option key={u.id} value={u.id}>
              Unit {u.unitNumber}
            </option>
          ))}
        </select>
      </Field>
      <button
        type="button"
        onClick={onRemove}
        className="px-2.5 py-2 rounded-md text-ink-500 hover:text-status-rejected-fg hover:bg-sand-50 mb-3"
        aria-label="Remove role grant"
      >
        <Trash2 size={14} />
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Roles list (edit panel)
// ---------------------------------------------------------------------------
function RolesList({
  userId,
  buildings,
  grants,
  onChanged,
}: {
  userId: string;
  buildings: Building[];
  grants: RoleGrant[];
  onChanged: () => void | Promise<void>;
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<{
    buildingId: string;
    roleCode: RoleCode;
    unitId: string;
  }>({ buildingId: "", roleCode: "unit_owner", unitId: "" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (adding && !draft.buildingId && buildings[0]) {
      setDraft((d) => ({ ...d, buildingId: buildings[0]!.id }));
    }
  }, [adding, buildings, draft.buildingId]);

  const grant = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/admin/users/${userId}/roles`, {
        buildingId: draft.buildingId,
        roleCode: draft.roleCode,
        // Forward unitId for both unit_owner (required) and
        // board_member (optional but typical) so the server can
        // create the unit_owners row and mirror the unit_owner
        // role grant. Previously this only fired for unit_owner,
        // so granting Board Member with a unit silently dropped
        // the unit.
        ...((draft.roleCode === "unit_owner" ||
          draft.roleCode === "board_member") &&
        draft.unitId
          ? { unitId: draft.unitId }
          : {}),
      });
      setAdding(false);
      setDraft({ buildingId: "", roleCode: "unit_owner", unitId: "" });
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? JSON.stringify(err.body) : String(err));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (grantId: string) => {
    try {
      const res = await fetch(`/api/admin/users/${userId}/roles/${grantId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          message?: string;
        };
        throw new Error(body.message ?? body.error ?? `HTTP ${res.status}`);
      }
      await onChanged();
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    }
  };

  return (
    <div>
      {grants.length === 0 ? (
        <p className="text-[12px] text-ink-400 mb-2">No role grants.</p>
      ) : (
        <ul className="divide-y divide-sand-100 mb-2">
          {grants.map((g) => (
            <li key={g.id} className="py-2.5 flex items-center gap-3 flex-wrap">
              <span className="badge bg-brand-100 text-brand-700">
                {g.roleName}
              </span>
              {g.implicit && (
                <span
                  className="badge bg-sand-100 text-ink-500 text-[10.5px]"
                  title="Inferred from an active unit ownership; end the ownership in the Units list to remove this role"
                >
                  via ownership
                </span>
              )}
              <span className="text-[12.5px] text-ink-700 flex items-center gap-1.5">
                <Building2 size={13} className="text-ink-400" />
                {g.buildingName}
              </span>
              <span className="ml-auto text-[11px] text-ink-400">
                {g.implicit ? "since" : "granted"}{" "}
                {new Date(g.grantedAt).toLocaleDateString()}
              </span>
              {!g.implicit && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => void revoke(g.id)}
                >
                  <Trash2 size={13} /> Revoke
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {error && <div className="alert-error">{error}</div>}

      {adding ? (
        <div className="bg-sand-50 border border-sand-100 rounded-md p-3 mb-2">
          <GrantRow
            grant={draft}
            buildings={buildings}
            onChange={(patch) => setDraft({ ...draft, ...patch })}
            onRemove={() => setAdding(false)}
          />
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              loading={busy}
              onClick={() => void grant()}
              disabled={
                !draft.buildingId ||
                (draft.roleCode === "unit_owner" && !draft.unitId)
              }
            >
              Grant
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setAdding(false);
                setError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] text-brand-700 hover:bg-brand-50"
        >
          <Plus size={13} /> Add role
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ownerships list (edit panel)
// ---------------------------------------------------------------------------
function OwnershipsList({
  userId,
  buildings,
  ownerships,
  onChanged,
}: {
  userId: string;
  buildings: Building[];
  ownerships: Ownership[];
  onChanged: () => void | Promise<void>;
}) {
  const [adding, setAdding] = useState(false);
  const [buildingId, setBuildingId] = useState<string>("");
  const [unitId, setUnitId] = useState<string>("");
  // Ownership type defaults to 'primary'. Switch to 'co_owner' when
  // adding a second person to a unit (joint ownership: spouses,
  // partnerships, etc.). Both confer the same edit / review
  // capabilities on the unit's projects — the type is a label,
  // not a permission gate.
  const [ownershipType, setOwnershipType] = useState<
    "primary" | "co_owner" | "investor"
  >("primary");
  const [units, setUnits] = useState<Unit[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (adding && !buildingId && buildings[0]) {
      setBuildingId(buildings[0].id);
    }
  }, [adding, buildings, buildingId]);

  useEffect(() => {
    if (!buildingId) return;
    void api
      .get<{ data: Unit[] }>(`/api/buildings/${buildingId}/units`)
      .then((r) => setUnits(r.data));
  }, [buildingId]);

  const add = async () => {
    if (!unitId) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/admin/users/${userId}/unit-owners`, {
        unitId,
        ownershipType,
      });
      setAdding(false);
      setUnitId("");
      setOwnershipType("primary");
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? JSON.stringify(err.body) : String(err));
    } finally {
      setBusy(false);
    }
  };

  const end = async (id: string) => {
    await api.del(`/api/admin/users/${userId}/unit-owners/${id}`);
    await onChanged();
  };

  return (
    <div>
      {ownerships.length === 0 ? (
        <p className="text-[12px] text-ink-400 mb-2">No units assigned.</p>
      ) : (
        <ul className="divide-y divide-sand-100 mb-2">
          {ownerships.map((o) => (
            <li key={o.id} className="py-2.5 flex items-center gap-3 flex-wrap">
              <span className="font-medium text-ink-800 text-[13px]">
                Unit {o.unitNumber}
              </span>
              <span className="text-[11.5px] text-ink-500">
                {o.buildingName}
              </span>
              <span className="badge bg-sand-100 text-ink-600">
                {o.ownershipType}
              </span>
              <span className="ml-auto text-[11px] text-ink-400">
                since {new Date(o.startedAt).toLocaleDateString()}
              </span>
              {/*
                White-glove submission: kicks the staff member into
                the New Project wizard with the target owner +
                target unit pre-selected. The wizard branches on
                ?onBehalfOfUserId=… to load this user's units
                instead of the principal's, and forwards the id
                to POST /api/projects so the server records who
                acted on whose behalf.
              */}
              <Link
                to={`/projects/new?onBehalfOfUserId=${userId}&unitId=${o.unitId}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] text-brand-700 hover:text-brand-800 hover:bg-brand-50 no-underline hover:no-underline"
                title="Start a project for this owner (white-glove)"
              >
                <Sparkles size={12} /> Start project
              </Link>
              {/*
                White-glove insurance upload: opens the admin
                insurance page with the existing policies listed
                and a fresh upload form. Same multipart shape as
                /api/my/liability-policies, attributed to the
                owner; audit logged as a staff action.
              */}
              <Link
                to={`/admin/users/${userId}/insurance`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] text-brand-700 hover:text-brand-800 hover:bg-brand-50 no-underline hover:no-underline"
                title="Upload an insurance certificate on this owner's behalf"
              >
                <Shield size={12} /> Upload insurance
              </Link>
              <Button size="sm" variant="ghost" onClick={() => void end(o.id)}>
                <Trash2 size={13} /> End
              </Button>
            </li>
          ))}
        </ul>
      )}

      {error && <div className="alert-error">{error}</div>}

      {adding ? (
        <div className="bg-sand-50 border border-sand-100 rounded-md p-3">
          <div className="field-row">
            <Field label="Building">
              <select
                className="select"
                value={buildingId}
                onChange={(e) => {
                  setBuildingId(e.target.value);
                  setUnitId("");
                }}
              >
                {buildings.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Unit *">
              <select
                className="select"
                value={unitId}
                onChange={(e) => setUnitId(e.target.value)}
                required
              >
                <option value="">Select a unit…</option>
                {units.map((u) => (
                  <option key={u.id} value={u.id}>
                    Unit {u.unitNumber}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Ownership type"
              hint="Use co-owner when adding a second person (spouses, partners) to a unit that already has an owner. Both confer the same edit and reviewing capabilities."
            >
              <select
                className="select"
                value={ownershipType}
                onChange={(e) =>
                  setOwnershipType(
                    e.target.value as "primary" | "co_owner" | "investor",
                  )
                }
              >
                <option value="primary">Primary owner</option>
                <option value="co_owner">Co-owner (joint ownership)</option>
                <option value="investor">Investor</option>
              </select>
            </Field>
          </div>
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              loading={busy}
              disabled={!unitId}
              onClick={() => void add()}
            >
              Assign
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] text-brand-700 hover:bg-brand-50"
        >
          <Plus size={13} /> Assign unit
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter pill — shared between role filters and the active-project /
// missing-insurance toggles. A toggle button rather than a checkbox so
// the filter bar reads as a row of chips at a glance.
// ---------------------------------------------------------------------------
function FilterPill({
  active,
  icon,
  onClick,
  children,
}: {
  active: boolean;
  icon?: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11.5px] border transition-colors",
        active
          ? "bg-brand-700 border-brand-700 text-white hover:bg-brand-800"
          : "bg-white border-sand-200 text-ink-700 hover:border-brand-200 hover:text-brand-700",
      )}
    >
      {icon}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Temp-password callout
// ---------------------------------------------------------------------------
function TempPasswordCallout({
  password,
  onDismiss,
}: {
  password: string;
  onDismiss: () => void;
}) {
  const copy = () => {
    navigator.clipboard?.writeText(password).catch(() => {});
  };
  return (
    <div className="border-2 border-status-review-fg/30 bg-status-review-bg/40 rounded-md p-4 mb-4">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full bg-status-review-fg/15 text-status-review-fg">
          <Mail size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-status-review-fg text-[13px] mb-1">
            Temporary password — shown once
          </div>
          <p className="text-[12px] text-ink-700 mb-2">
            Send this to the user out-of-band. They can change it from
            Settings after their first sign-in.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <code className="px-2 py-1.5 bg-white border border-sand-200 rounded-sm text-[13px] select-all">
              {password}
            </code>
            <Button size="sm" variant="secondary" onClick={copy}>
              <Copy size={13} /> Copy
            </Button>
            <Button size="sm" variant="ghost" onClick={onDismiss}>
              Dismiss
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

